import { useEffect, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { LeadTimeSettingsResponse, SafetySettingsResponse } from "@march/shared";
import { apiGet } from "../lib/api.ts";
import { theme } from "../theme.ts";

/**
 * The training guide, in the app (it replaces the Notion page). Plain
 * language, real examples. Lead times and safety-buffer targets are read live
 * from the settings, so those never go stale; other figures are examples as of
 * the date shown and are labelled as such.
 *
 * Update this file when a model, setting or screen changes.
 */
const AS_OF = "22 Oct 2026";

const SECTIONS = [
  ["start", "1. What the app is for"],
  ["ideas", "2. Four ideas to understand first"],
  ["data", "3. Where the numbers come from"],
  ["demand", "4. How demand is counted"],
  ["screens", "5. Screen by screen"],
  ["models", "6. The forecast models"],
  ["order", "7. Need to order"],
  ["leadtimes", "8. Lead times: what the data shows"],
  ["stockouts", "9. Stockouts and lost sales"],
  ["routine", "10. A simple routine"],
  ["ai", "11. AI assistants and the API"],
  ["glossary", "12. Glossary"],
  ["faq", "13. Common questions"],
  ["limits", "14. Known limits"],
] as const;

export function GuidePage() {
  const loc = useLocation();
  // Deep links: /guide#leadtimes
  useEffect(() => {
    if (loc.hash) document.getElementById(loc.hash.slice(1))?.scrollIntoView();
  }, [loc.hash]);

  return (
    <div className="guide-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: theme.space.xl, alignItems: "start" }}>
      <article style={{ maxWidth: 860, lineHeight: 1.6, fontSize: theme.font.size.md, color: theme.color.text }}>
        <Callout tone="info">
          <b>New to the app? Start here.</b> This guide explains every screen, number and model in plain language, with
          real examples. Lead times and safety-buffer targets below are <b>live</b> from the settings; other figures are
          examples as of {AS_OF} and will differ from what you see today.
        </Callout>

        <Start />
        <Ideas />
        <Data />
        <Demand />
        <Screens />
        <Models />
        <Order />
        <LeadTimes />
        <Stockouts />
        <Routine />
        <AI />
        <Glossary />
        <Faq />
        <Limits />
      </article>

      <nav className="guide-nav"
        style={{
          position: "sticky",
          top: theme.space.lg,
          fontSize: theme.font.size.sm,
          display: "grid",
          gap: 2,
          borderLeft: `2px solid ${theme.color.border}`,
          paddingLeft: theme.space.md,
        }}
      >
        <div style={{ color: theme.color.textMuted, fontWeight: 600, marginBottom: 4 }}>Contents</div>
        {SECTIONS.map(([id, title]) => (
          <a key={id} href={`#${id}`} style={{ color: theme.color.textMuted, textDecoration: "none", padding: "2px 0" }}>
            {title}
          </a>
        ))}
      </nav>
    </div>
  );
}

// ─── building blocks ───────────────────────────────────────────

function H2({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      style={{
        fontSize: theme.font.size.xl,
        margin: `${theme.space.xxl}px 0 ${theme.space.sm}px`,
        paddingTop: theme.space.sm,
        borderTop: `1px solid ${theme.color.border}`,
        scrollMarginTop: 16,
      }}
    >
      {children}
    </h2>
  );
}
const H3 = ({ children }: { children: ReactNode }) => (
  <h3 style={{ fontSize: theme.font.size.lg, margin: `${theme.space.lg}px 0 ${theme.space.xs}px` }}>{children}</h3>
);

function Callout({ tone = "note", children }: { tone?: "info" | "note" | "warn"; children: ReactNode }) {
  const bg = tone === "info" ? theme.color.brandFaint : tone === "warn" ? theme.color.warningFaint : theme.color.bg;
  return (
    <div style={{ background: bg, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, padding: "10px 14px", margin: `${theme.space.md}px 0` }}>
      {children}
    </div>
  );
}

function Details({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details style={{ margin: `${theme.space.sm}px 0`, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, padding: "6px 12px" }}>
      <summary style={{ cursor: "pointer", fontWeight: 600 }}>{summary}</summary>
      <div style={{ paddingTop: 6 }}>{children}</div>
    </details>
  );
}

const cell: CSSProperties = { padding: "6px 10px", borderBottom: `1px solid ${theme.color.border}`, verticalAlign: "top", textAlign: "left" };

function Table({ head, rows }: { head: ReactNode[]; rows: ReactNode[][] }) {
  return (
    <div style={{ overflowX: "auto", margin: `${theme.space.sm}px 0` }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: theme.font.size.base }}>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={i} style={{ ...cell, background: theme.color.bg, fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} style={cell}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Code = ({ children }: { children: ReactNode }) => (
  <code style={{ fontFamily: theme.font.mono, background: theme.color.bg, padding: "1px 5px", borderRadius: 4, fontSize: "0.92em" }}>{children}</code>
);
const Pre = ({ children }: { children: string }) => (
  <pre style={{ fontFamily: theme.font.mono, background: theme.color.bg, padding: 10, borderRadius: 6, fontSize: theme.font.size.sm, overflowX: "auto" }}>{children}</pre>
);
const To = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} style={{ color: theme.color.brand }}>
    {children}
  </Link>
);

// ─── live settings ─────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CAT_LABEL: Record<string, string> = {
  Frame: "Frames",
  Base: "Bases",
  Fabric: "Fabric",
  Cover: "Sunbrella covers",
  ImportedCover: "Solstice & Pacifica Marine covers",
  Trim: "Trim",
  ProtectiveCover: "Protective covers",
};
const CATS = ["Frame", "ImportedCover", "Base", "Fabric", "Cover", "Trim", "ProtectiveCover"] as const;

function seasonText(months: number[]): string {
  if (!months.length) return "no season set";
  const s = [...months].sort((a, b) => a - b);
  return `${MONTHS[s[0] - 1]}–${MONTHS[s[s.length - 1] - 1]}`;
}

function LiveLeadTimes() {
  const q = useQuery<LeadTimeSettingsResponse>({
    queryKey: ["lead-settings"],
    queryFn: () => apiGet("/api/settings/lead-times"),
  });
  if (!q.data) return <p style={{ color: theme.color.textMuted }}>Loading current lead times…</p>;
  const s = q.data.settings;
  const season = seasonText(s.season_months);
  const why: Record<string, string> = {
    Frame: "Overseas (Smart Fit, Shyh Hwa). Off season they're built to order; in season they ship from the vendor's season stock — see section 8",
    Base: "Overseas, like frames",
    Fabric: "Bought locally (Glen Raven, Trivantage); a little slower off season — see section 8",
    Cover: "Cut and sewn in-house (2–3 days, 5–7 at peak). Their fabric has its own lead time",
    ImportedCover: "Bought finished from Smart Fit, shipped with frames. Same as frames off season; slower in season because they're made to order — see section 8",
    Trim: "Binding, fringe and tassels, bought in yards from overseas on the August programme",
    ProtectiveCover: "Umbrella storage covers, from overseas",
  };
  return (
    <>
      <Table
        head={["Category", "Off season", `In season (${season})`, "Why"]}
        rows={CATS.map((k) => [
          <b>{CAT_LABEL[k]}</b>,
          `${s.categories[k].off} days`,
          `${s.categories[k].in} days`,
          why[k],
        ])}
      />
      {q.data.overrides && q.data.overrides.length > 0 && (
        <p>
          <b>SKU overrides</b> (apply all year):{" "}
          {q.data.overrides.map((o, i) => (
            <span key={o.itemid}>
              {i > 0 && " · "}
              <Code>{o.itemid}</Code> {o.lead_time_days} days
            </span>
          ))}
        </p>
      )}
      <p style={{ color: theme.color.textMuted, fontSize: theme.font.size.sm }}>
        Live from Admin → Lead times
        {q.data.updated_at ? `, last changed ${new Date(q.data.updated_at).toLocaleDateString()}${q.data.updated_by ? ` by ${q.data.updated_by}` : ""}` : ""}.
      </p>
    </>
  );
}

function LiveSafety() {
  const q = useQuery<SafetySettingsResponse>({
    queryKey: ["safety-settings"],
    queryFn: () => apiGet("/api/settings/safety"),
  });
  if (!q.data) return <p style={{ color: theme.color.textMuted }}>Loading current safety-buffer targets…</p>;
  const s = q.data.settings;
  const meaning = s.mode === "fill" ? "fill rate (share of demand shipped from stock)" : "no stockout in that share of lead-time periods";
  return (
    <>
      <Table
        head={["Category", "Target", "Meaning"]}
        rows={CATS.map((k) => [
          <b>{CAT_LABEL[k]}</b>,
          s.targets[k] > 0 ? `${Math.round(s.targets[k] * 100)}%` : "No buffer",
          s.targets[k] > 0 ? meaning : "Not buffered",
        ])}
      />
      <p style={{ color: theme.color.textMuted, fontSize: theme.font.size.sm }}>Live from Admin → Safety buffer.</p>
    </>
  );
}

// ─── sections ──────────────────────────────────────────────────

function Start() {
  return (
    <>
      <H2 id="start">1. What the app is for</H2>
      <p>
        The app helps buyers and ops answer three questions about <b>parts</b> — frames, covers, bases and fabric:
      </p>
      <ol>
        <li>
          <b>What do we have?</b> Stock on the shelf, open purchase orders, containers on the water.
        </li>
        <li>
          <b>What will we need?</b> A forecast of demand for every tracked part.
        </li>
        <li>
          <b>What should we do now?</b> Which parts to order, and which incoming orders to expedite.
        </li>
      </ol>
      <H3>First, how an umbrella is put together</H3>
      <p>We sell finished umbrellas, but we <b>buy and stock the parts</b> they're made from. This app is about the parts:</p>
      <Table
        head={["Part", "What it is", "Where it comes from", "Example code"]}
        rows={[
          [<b>Frame</b>, "The pole, ribs and mechanism", "Made overseas (mainly Smart Fit in Asia), shipped by container", <Code>ALTO908-002</Code>],
          [<b>Cover — Sunbrella</b>, "The canopy that goes on the frame", "Cut and sewn in-house from Sunbrella fabric", <Code>C908-5401</Code>],
          [<b>Cover — Solstice & Pacifica Marine</b>, "The canopy, in Solstice (Olefin) or Pacifica Marine fabric", "Bought finished from overseas (Smart Fit), on the same containers as frames", <span><Code>C908-F04</Code>, <Code>C908-SA04</Code></span>],
          [<b>Fabric</b>, "The cloth Sunbrella covers are sewn from, measured in yards", "Bought from local suppliers (Glen Raven, Trivantage)", <Code>5404</Code>],
          [<b>Base</b>, "What holds the umbrella up", "Made overseas (Smart Fit), shipped by container with frames", <Code>TB60</Code>],
          [<b>Trim</b>, "Binding, fringe and tassels sewn onto some covers, measured in yards", "Made overseas, shipped by container", <Code>BDNATURAL</Code>],
          [<b>Protective cover</b>, "A storage cover that goes over a closed umbrella", "Made overseas, shipped by container", <Code>PC301</Code>],
        ]}
      />
      <p>
        So one umbrella sold uses one frame, one cover and often a base. A Sunbrella cover also used some yards of fabric; a
        Solstice or Pacifica Marine cover arrives already made, so we never order its fabric on its own. If any part runs out,
        we can't build the umbrella — that's what this app helps prevent.
      </p>
      <p>
        Every part has a <b>SKU</b> — its item code, like <Code>ALTO908-002</Code>. The app tracks about 1,000 of them: roughly
        136 frames, 169 covers, 275 "cover patterns" (explained in section 4), 48 bases, 352 fabrics, 18 trims and 15 protective
        covers.
      </p>
      <H3>Your first visit: a 10-minute tour</H3>
      <ol>
        <li>
          Start on <To to="/">Home</To>. <b>Buying</b> shows how many parts to order now, which POs to ask to ship sooner and
          the top-ups that can wait; <b>Receiving at Jurupa</b> shows late and this week's deliveries. Click a card to open the list.
        </li>
        <li>
          Open <To to="/forecast">Forecast</To> and stay on the <b>Frames</b> tab. It opens on <b>Needs attention</b>: only the
          frames that need an order, a faster delivery or a chase. The coloured buttons above the list filter it (Order now,
          Ship sooner, Late PO…); the product-line buttons at the bottom open everything else.
        </li>
        <li>
          Read one row left to right: <b>Current stock</b>, <b>Forecast use</b> for the next 90 days (red where it's more than the
          stock on the shelf), <b>On order</b> with the date each delivery lands, when the shelf <b>Runs out</b>, and{" "}
          <b>Need to order</b> at the far right. "—" means nothing to buy.
        </li>
        <li>
          Click any row. A panel opens with the part's stock chart and how its Need to order is worked out, step by step.
        </li>
        <li>
          Open <To to="/trends">Trends</To>. It shows what customers are choosing (finishes, series, sizes, cover lines and
          colours) against the same weeks last year. Numbers only: To order and Arriving are the pages that say what to do.
        </li>
        <li>
          Open <To to="/demand">Yearly Demand</To>. <b>Overview</b> shows which product lines grew or shrank; <b>One part</b>{" "}
          looks up <Code>ALTO908-002</Code> month by month.
        </li>
      </ol>
      <p>Then read sections 2 and 7 of this guide — they explain the two numbers that matter most.</p>
      <Callout>
        It covers <b>March Products</b> — the Jurupa and Texas warehouses, plus Type B (the NetSuite location for bulk and
        Amazon orders). <b>Gold Crest LLC is a separate company</b> and is never added into March numbers; it isn't shown in the app for now.
      </Callout>
      <H3>Getting in</H3>
      <ul>
        <li>
          <b>Website:</b> <Code>forecast.marchproducts.com</Code>. Sign in with your <b>March Microsoft account</b> — the
          same one as Outlook and Teams. No separate password.
        </li>
        <li>Only March accounts can sign in; guest accounts are refused. A disabled account loses access within the hour.</li>
        <li>
          Everyone starts as a <b>viewer</b> (every screen). <b>Admins</b> can also change settings and manage agent access.
          Ask an admin to change your role.
        </li>
        <li>
          You stay signed in for up to 90 days of use. <b>Sign out</b> is under your name at the bottom of the sidebar.
        </li>
      </ul>
    </>
  );
}

function Ideas() {
  return (
    <>
      <H2 id="ideas">2. Four ideas to understand first</H2>
      <H3>Idea 1 — the app counts units used, not units sold</H3>
      <p>
        Every part that <b>left the building</b> counts, whether it was sold on its own or went out inside an umbrella.
        That's the number you need for buying parts.
      </p>
      <Callout>
        <b>Example: frame ALTO908-002, FY2026 (Jul 2025 – Jun 2026)</b>
        <ul style={{ margin: "6px 0" }}>
          <li>
            Sold by itself: <b>14</b>
          </li>
          <li>
            Went out inside finished umbrellas: <b>2,897</b> (70 different umbrella SKUs use this frame)
          </li>
          <li>
            <b>Total used: 2,911</b> ← the number the app shows
          </li>
        </ul>
        Every one of those umbrellas needed a frame, so 2,911 is the number to buy against.
      </Callout>
      <Callout tone="warn">
        <b>Comparing with NetSuite?</b> NetSuite's sales reports (such as <i>Sales by Item</i>) count a frame only when it
        was sold by itself — they'd show <b>14</b> here, because the other 2,897 are recorded as umbrella sales. Neither is
        wrong; they answer different questions. The app shows the "sold by itself" figure too, as <b>"N sold alone"</b>{" "}
        under each total in Yearly Demand's <b>Every part</b> view.
      </Callout>
      <H3>Idea 2 — the forecast looks 90 days ahead; ordering looks one lead time ahead</H3>
      <p>
        The forecast shows the next <b>90 days</b> so you can watch the season. "Need to order" looks only as far as each
        part's <b>lead time</b> — how long a new order takes to arrive — and counts only stock that lands within it. We
        order on demand, so supply only has to last until a new order could land.
      </p>
      <Callout>
        <b>Example.</b> A frame ordered today in the off season takes about 150 days to arrive. So the app asks: "Will what we
        have, plus what's already coming in the next 150 days, cover what we'll use in the next 150 days?" If not, Need to order
        shows the gap. Anything needed further out can be ordered later, when it comes within reach.
      </Callout>
      <H3>Idea 3 — every forecast is wrong by some amount, so parts carry a buffer</H3>
      <p>
        A forecast is a middle estimate. Every part except Sunbrella covers (which we sew in days) carries a{" "}
        <b>safety buffer</b> to cover a busier-than-expected stretch (section 7).
      </p>
      <H3>Idea 4 — you can check every number</H3>
      <p>
        Click a fiscal-year figure on <To to="/demand">Yearly Demand</To> to see exactly how it's built. <To to="/accuracy">Forecast accuracy</To>{" "}
        shows how good past forecasts were. A badge on the Yearly Demand page checks the sales numbers against NetSuite every night.
      </p>
    </>
  );
}

function Data() {
  return (
    <>
      <H2 id="data">3. Where the numbers come from</H2>
      <p>
        The app doesn't hold anything you type in. It copies data from two company systems and does the maths itself:
      </p>
      <ul>
        <li>
          <b>NetSuite</b> — the company's main business system: stock, purchase orders (POs — what we've ordered from
          suppliers), sales invoices.
        </li>
        <li>
          <b>Synkio</b> — tracks shipping containers on their way from overseas, with each container's estimated arrival date
          (its <b>ETA</b>).
        </li>
      </ul>
      <Table
        head={["Data", "Source", "Refreshed"]}
        rows={[
          ["Stock on hand, open purchase orders", "NetSuite", "Every hour"],
          ["Demand (invoices, cash sales, credit memos)", "NetSuite", "Hourly for the last 10 days; nightly for this month and last month in full"],
          ["Containers on the water, with ETAs", "Synkio (container tracking)", "Every hour"],
          ["Bills of materials (what's in each umbrella)", "NetSuite", "Nightly"],
          ["Forecast, safety buffers, cancelled orders, stockout calendar", "Calculated by the app", "Nightly (~11 PM Pacific)"],
          ["Parts inside built umbrellas", "Calculated by the app", "Every hour"],
        ]}
      />
      <ul>
        <li>
          The app keeps <b>daily totals per part</b>, not individual invoices, so pages stay fast even when NetSuite is slow.
        </li>
        <li>
          The <b>invoice drill-down</b> (Yearly Demand → One part → click a month) is fetched live from NetSuite.
        </li>
        <li>
          <b>Tie-out badge</b> (top of the Yearly Demand page): every night the app asks NetSuite separately how many of each part
          were sold by itself, and compares. Green = match; amber lists differences.
        </li>
      </ul>
    </>
  );
}

function Demand() {
  return (
    <>
      <H2 id="demand">4. How demand is counted</H2>
      <H3>Frames: three ways a frame shows up</H3>
      <Table
        head={["Path", "What the invoice shows", "ALTO908-002, FY2026"]}
        rows={[
          [<b>Sold on its own</b>, "A priced line for the frame", "14"],
          [<b>Itemised inside an umbrella order</b>, "The umbrella line, with the frame also listed at $0", "2,897 together"],
          [<b>Through the umbrella's parts list</b>, "Only the umbrella line; the app looks up its bill of materials (BOM) and counts one frame per umbrella", "(included above)"],
          [<b>Total used</b>, "", <b>2,911</b>],
        ]}
      />
      <Details summary="Example invoices">
        <p>Itemised — the frame is on the invoice, so it's counted directly:</p>
        <Pre>{`INV-1001
  ALTO908002-F03   umbrella   qty 10   $1,500.00
  ALTO908-002      frame      qty 10       $0.00
  C908-F03         cover      qty 10       $0.00`}</Pre>
        <p>Parts list — only the umbrella is on the invoice; the app adds 10 frames from its BOM:</p>
        <Pre>{`INV-2002
  ALTO908002-F03   umbrella   qty 10   $1,500.00`}</Pre>
        <p>
          A frame already itemised on an invoice is <b>not</b> counted again. Every unit counts once.
        </p>
      </Details>
      <Callout>
        Around <b>March 2026</b>, umbrella invoices stopped listing components as $0 lines. Older months show units under
        "In umbrella orders"; newer months under "Via umbrella BOM". Same frames — only the paperwork changed.
      </Callout>
      <H3>Covers: same three paths, plus two rules</H3>
      <ul>
        <li>
          <b>Two kinds of cover SKU.</b> Solid-colour covers like <Code>C908-F04</Code> are tracked one by one.{" "}
          <b>Cover patterns</b> like <Code>C908-5401</Code> (9 ft cover in Sunbrella 5401) add together the standard versions
          — codes ending -SWV (single wind vent) and -DWV (double) — and forecast them as one.
        </li>
        <li>
          <b>Solstice (Olefin) and Pacifica Marine covers are imported finished.</b> Their fabric isn't stocked or re-ordered here, so no fabric
          demand comes from them — and they're planned like frames (overseas lead times, safety buffer). That's why Yearly Demand and
          the planning workbook keep Solstice (Olefin), Pacifica Marine and Sunbrella covers apart.
        </li>
        <li>
          <b>11 ft Solstice and Pacifica Marine covers are single vent now.</b> They came with a double wind vent until 2025
          (e.g. <Code>C118-F04-DWV</Code>), then switched to single (<Code>C118-F04-SWV</Code>). Only the single-vent covers are
          tracked, and the double-vent sales from before the switch count as the single-vent cover's — so its history, forecast
          and trends include last season. A colour with no single-vent item in NetSuite (e.g. SA14) has no 11 ft cover tracked.
        </li>
        <li>
          <b>Custom covers aren't added in</b> — logo, two-tone, and codes ending -LP or -LH. This matches the planning workbook.
        </li>
        <li>
          Covers sell on their own more than frames (replacements): <Code>C908-F04</Code> in FY2026 = 103 on its own + 3,866
          inside 30 umbrella models = <b>3,969</b>.
        </li>
      </ul>
      <H3>Fabric: one level further down</H3>
      <p>
        A cover's number never includes its fabric. Fabric is counted two levels down — <b>umbrella → cover → fabric</b>{" "}
        ("L2" = level 2) — and measured in <b>yards</b>.
      </p>
    </>
  );
}

function Screens() {
  return (
    <>
      <H2 id="screens">5. Screen by screen</H2>
      <p>
        Every list page has the same layout: tabs along the top (with how many parts each holds), coloured buttons that filter
        the list, then a search box and <b>Export</b> above the table. Switches that change the whole page (company, Simple /
        More detail, views) sit at the top right. Click any row to open its panel. Nothing in the app changes NetSuite.
      </p>
      <H3>
        <To to="/">Home</To>, <To to="/order">To order</To> and <To to="/arriving">Arriving</To> — "what do I do today?"
      </H3>
      <ul>
        <li>
          <b>Home</b> — <b>Buying</b> (parts to order now, POs to ask to ship sooner, top-ups that can wait) and{" "}
          <b>Receiving at Jurupa</b> (late deliveries, this week's, stalled POs). Click a card to open the list.
        </li>
        <li>
          <b>To order</b> — three tabs. <b>Order now</b>: parts that run out before a new order could arrive.{" "}
          <b>Ship sooner</b>: POs and containers that land after the shelf runs empty — ask the supplier to bring them forward.{" "}
          <b>Top-ups</b>: below the safety buffer but they'll last until a new order arrives. Tick parts to change quantities,
          then Export; with nothing ticked, Export takes the whole list.
        </li>
        <li>
          <b>Arriving</b> — deliveries to Jurupa: late ones first, then week by week (tabs for the next 2 or 4 weeks, and POs with
          no date). Stalled POs are grouped apart. Open a PO or container to see every line.
        </li>
        <li>
          <b>Data status</b> (under Help) — when stock last synced from NetSuite, when the forecast last ran, last night's
          NetSuite tie-out. If something is stale, numbers elsewhere may be too.
        </li>
      </ul>
      <Callout>
        Some parts are left off To order at Operations' request (they aren't monitored or bought for stock; the list is on
        Admin → Not stocked).
        Each page says how many and has a <b>Show them</b> link. The Forecast page still lists them. AI agents get the same
        list: their buying list leaves these parts out too and says how many.
      </Callout>

      <H3>
        <To to="/forecast">Forecast</To> — "what do we need?"
      </H3>
      <p>
        Tabs for <b>Frames, Covers, Bases, Fabric, Trim and Protective covers</b> (fabric and trim in yards). The{" "}
        <b>Simple</b> view opens on <b>Needs attention</b>; the buttons above the list filter by status:
      </p>
      <Table
        head={["Status", "Meaning"]}
        rows={[
          [<b>Order now</b>, "On hand plus what lands in time won't last the lead time: raise a PO (was the \"order\" tag)"],
          [<b>Ship sooner</b>, "Stock is on order but the shelf empties before it lands: decide whether to expedite (was \"expedite?\")"],
          [<b>Late PO</b>, "A delivery's expected date has passed. Stalled POs (30+ days late, not shipped) are included; see section 7"],
          [<b>Top-up</b>, "Below the safety buffer only; it lasts until a new order would land"],
          [<b>Over a year of stock</b>, "More than 365 days of stock on the shelf"],
          [<b>OK</b>, "Nothing to do"],
        ]}
      />
      <Table
        head={["Column", "Meaning"]}
        rows={[
          [<b>Current stock</b>, "Loose stock at Jurupa + Texas minus what's promised to customer orders (\"Comp Avail\"). Bar in solid blue"],
          [<b>Forecast use</b>, "Expected use over the next 90 days. Light blue up to the stock on the shelf, red for the part stock doesn't cover"],
          [<b>On order</b>, "Each delivery with the date it lands (container ETA + 9 days, or the PO's expected date). Striped = not here yet; amber = late"],
          [<b>Runs out</b>, "When the shelf empties, counting deliveries as they land, and when stock comes back"],
          [<b>Need to order</b>, <span><b>The buying number</b> (section 7). It looks one lead time ahead, not 90 days</span>],
        ]}
      />
      <p>
        <b>More detail</b> (top right) shows the full table: In built umbrellas, Days exposed, On water, Total supply, prior years,
        the model tag, Confidence, Lead-time demand and Safety buffer, plus the Low on-hand, Need to order only and Stalled POs
        filters. Hover any dotted number there for how it was worked out.
      </p>

      <H3>
        <To to="/trends">Trends</To> — "what are customers choosing?"
      </H3>
      <p>
        Spots in-season shifts in taste: which frame finishes, series and sizes, and which cover lines and colours, are gaining
        or losing. <b>Numbers only</b> — no flags or advice; you (or the AI assistant) decide what a move means.
      </p>
      <ul>
        <li>
          Pick a category, then what to group by (e.g. Frames by <b>Finish</b>, Covers by <b>Colour / fabric</b>). Each row is
          one finish or colour: units in the last 4 (or 8) weeks, the same weeks last year, the change, its <b>share</b> of the
          category (the bar; the tick marks last year's share), how much that share moved, and the latest weeks against the ones
          before. The small chart is 13 weeks, this year solid and last year dashed. Click a column heading to sort by it.
        </li>
        <li>
          <b>Share</b> is the clearest signal: it compares like with like across the season. Example (late Oct 2026, inside
          umbrellas): Bronze (117) frames were 58% of frames, against 37.5% in the same weeks of 2025.
        </li>
        <li>
          Click a row to see the parts behind it — a big move from one part (or one customer's order) reads differently from a
          move spread across many.
        </li>
        <li>
          <b>In umbrellas</b> counts only parts that went out inside finished umbrellas (what customers picked); <b>All use</b>{" "}
          adds parts sold on their own, mostly spares. Parts Operations doesn't stock still count here, marked "not stocked" —
          demand for them is a trend too.
        </li>
        <li>
          Percentages built on only a handful of units are shown in <b>grey</b> — one order can swing them. Off season,
          volumes are low, so more numbers are grey.
        </li>
        <li>
          Not shown yet: how many <b>orders and customers</b> a number came from. Until it is, use the drill-down to see
          whether a move is one part or many.
        </li>
      </ul>

      <Callout>
        <b>Which pages tell you what to do?</b> Only two: <b>To order</b> (what to buy) and <b>Arriving</b> (what to receive,
        chase or expedite). Every other page — Forecast, Trends, Yearly Demand, Forecast accuracy — gives numbers, never advice.
      </Callout>
      <p className="muted">
        <b>Monitor</b> (the 30-day Urgent / Watch check) and its <b>Alerts</b> were retired on 23 Oct 2026: their red and
        yellow flags were noisy off season and repeated what To order and Arriving already say. Trends shows the same use as
        data; where the stock sits (Jurupa, Texas) and the last 30 days' use are in each part's panel.
      </p>

      <H3>
        <To to="/demand">Yearly Demand</To> — "what happened?"
      </H3>
      <p>
        Tabs: Frames, Solstice covers, Pacifica Marine covers, Sunbrella covers, Bases, Trim (yards) and Protective covers. The
        switch at the top right picks the view:
      </p>
      <ul>
        <li>
          <b>Overview</b> — the lines growing and shrinking most, then one row per product line by fiscal year, with this year so
          far against its straight-line projection. Click a line for its yearly chart and its parts, grouped by size or by
          finish / fabric.
        </li>
        <li>
          <b>Compare lines</b> — every product line on one graph; switch lines on and off.
        </li>
        <li>
          <b>Every part</b> — every SKU by fiscal year, like the planning workbook, with "N sold alone" under each total. Click a
          total for "How this number is built".
        </li>
        <li>
          <b>One part</b> — month by month: sold alone, inside umbrellas, L2 fabric, total, <b>cancelled orders</b>,{" "}
          <b>days out of stock</b>. Click a month for the actual invoices (live from NetSuite).
        </li>
        <li>
          <b>Notes</b> — why numbers can differ from the planning workbook (link under the table).
        </li>
      </ul>

      <H3>
        <To to="/accuracy">Forecast accuracy</To> — "how good is the forecast?"
      </H3>
      <p>
        Every week the app saves a copy of the forecast; when its 90 days (a <b>window</b>) are up, it's scored against what
        was actually used. The page shows: this window so far, the track record, every model's score, and confidence by SKU.
      </p>
      <Details summary="How confidence grades work">
        <ul>
          <li>
            <b>High</b> — 60%+ of past windows within ±25%.
          </li>
          <li>
            <b>Medium</b> — most within ±50%.
          </li>
          <li>
            <b>Low</b> — often off by more than 50%. Use judgement.
          </li>
          <li>
            <b>Low volume</b> / <b>New</b> — too little to judge.
          </li>
        </ul>
      </Details>
      <Callout>
        <b>What to expect:</b> category totals land within about 10%. Single SKUs are rougher — about a third within ±25%,
        over half within ±50%. That's why buffers exist.
      </Callout>

      <H3>Admin (admins only)</H3>
      <ul>
        <li>
          <b>Lead times</b> — per category (frames, bases, fabric, Sunbrella covers, Solstice &amp; Pacifica Marine covers, trim,
          protective covers), off and in season, which months are season, and per-SKU overrides.
        </li>
        <li>
          <b>Safety buffer</b> — target per category.
        </li>
        <li>
          <b>Not stocked</b> — the parts Operations doesn't keep in stock: cover colours (every size) and single parts. They
          stay forecast but are left off To order and out of the AI agents' buying list. A change shows on the next page load.
        </li>
        <li>
          <b>Agent access</b> — Claude connector address, API keys, and a log of sign-ins and agent activity.
        </li>
        <li>Data loading and refresh logs.</li>
      </ul>
      <p>Saving a setting recalculates the forecast within about a minute. Every change is recorded with who and when.</p>
    </>
  );
}

function Models() {
  return (
    <>
      <H2 id="models">6. The forecast models</H2>
      <p>
        A <b>forecast</b> is the app's best estimate of how many of each part we'll use in the next 90 days. A <b>model</b> is
        the method used to work that out. You don't need the details to use the app — this section is for when you want to
        know why a number is what it is.
      </p>
      <Table
        head={["Tab", "Model", "Tag", "In plain words"]}
        rows={[
          ["Frames, Covers (all kinds)", <b>Top-down split</b>, "SPLIT", "Forecast the whole category, then share it out by each SKU's share of the last 12 months"],
          ["Trim, Protective covers", <b>Top-down split</b>, "SPLIT", "Same method; new as separate categories (Oct 2026), so their own track record is short"],
          ["Bases", <b>Top-down × category pace</b>, "SPLIT·PACE", "Same, then nudged toward how bases have actually been selling in the last 28 days"],
          ["Fabric", <b>Top-down + cancelled orders</b>, "SPLIT+", "Same, but orders that were cancelled count as demand"],
        ]}
      />
      <Details summary="How the category forecast is made">
        <ol>
          <li>Take the same 90 days in each of the last 3 years for the whole category.</li>
          <li>Average them.</li>
          <li>Adjust for growth: last 12 months vs the 12 before.</li>
          <li>Share the total out by each SKU's share of the last 12 months.</li>
        </ol>
        <p>
          Category totals are steady; single SKUs jump on one-off orders. Splitting a steady total was more accurate in testing.
        </p>
      </Details>
      <Details summary="How pace works (Bases)">
        <p>
          Nightly: actual base use over the last 28 days (one-off spikes capped) ÷ what the forecast expected for those days.
          1.0 = on plan, 1.2 = 20% hot. The forecast moves <b>halfway</b> toward it, limited to ×0.75–×1.5.
        </p>
      </Details>
      <Details summary={`Every model tested (weighted error, lower is better — as of ${AS_OF})`}>
        <Table
          head={["Model", "Frames", "Covers", "Bases", "Fabric"]}
          rows={[
            ["Same-window formula (original)", "36%", "40%", "36%", "30%"],
            ["Tuned formula", "32%", "38%", "37%", "24%"],
            [<b>Top-down split</b>, <b>30% ✓</b>, <b>34% ✓</b>, "31%", "25%"],
            [<b>Top-down × pace</b>, "31%", "34%", <b>30% ✓</b>, "22%"],
            [<b>Top-down + cancelled orders</b>, "34%", "36%", "38%", <b>21% ✓</b>],
            ["Seasonal smoothing", "48%", "49%", "42%", "33%"],
            ["Stockout-adjusted", "31%", "36%", "36%", "25%"],
          ]}
        />
        <p>
          Each model was re-run on 55 past weekly dates using only the data known then. All keep being scored weekly on live
          results, so a tab can switch when the evidence changes — see the Accuracy page for current scores.
        </p>
      </Details>
    </>
  );
}

function Order() {
  return (
    <>
      <H2 id="order">7. Need to order</H2>
      <Callout tone="info">
        <b>Need to order</b> = demand over the lead time + safety buffer − on hand − stock arriving within the lead time
        (when positive).
      </Callout>
      <Details summary="A worked example, step by step (ALTO908-002, as of 19 Oct 2026)">
        <ol>
          <li>
            <b>Demand over the lead time:</b> the app expects about <b>218</b> of this frame to be used over the lead time.
          </li>
          <li>
            <b>Safety buffer:</b> its past forecasts were off by about 17%, so it adds <b>42</b> extra.
          </li>
          <li>
            <b>Needed:</b> 218 + 42 = <b>260</b>.
          </li>
          <li>
            <b>Have or coming in time:</b> 865 on the shelf + 1,190 on order arriving in time = <b>2,055</b>.
          </li>
          <li>
            <b>Need to order:</b> 260 − 2,055 is below zero, so <b>0</b> — nothing to buy now.
          </li>
        </ol>
      </Details>
      <H3>Only stock that arrives in time counts</H3>
      <p>Stock on order or at sea counts toward Need to order only if it lands within the lead time:</p>
      <ul>
        <li>
          <b>Containers at sea</b> — Synkio's ETA + 9 days (the measured gap between ETA and NetSuite receipt).
        </li>
        <li>
          <b>Ordered but not yet shipped</b> — the PO's expected date. A PO with no date set still counts.
        </li>
      </ul>
      <p>
        Why: each August a large pre-season frame PO (42–57k units) is released in 40+ containers from October to June. Counted
        all at once, a SKU looked covered in November by units landing in May. Click the part to see what lands
        after the lead time — often better pulled forward with the supplier than ordered again.
      </p>
      <H3>Stalled POs</H3>
      <p>
        A PO line is <b>stalled</b> when its expected date passed more than 30 days ago and none of it is on a container yet.
        Home's <b>Stalled</b> card and Arriving's Stalled group list them; on the Forecast they count as <b>Late PO</b>, and More
        detail keeps the red <b>PO stalled</b> badge and the <b>Stalled POs</b> filter.
      </p>
      <ul>
        <li>
          <b>Stalled stock still counts toward Need to order</b> — for now. Many NetSuite expected dates are placeholders (one
          date per SKU on a pre-season PO that's really delivered through spring), so dropping them would trigger re-orders of
          stock that's on schedule.
        </li>
        <li>
          <b>What to do:</b> ask the supplier. If it's coming, put the real date on the PO in NetSuite. If it isn't, close the
          line — the app then stops counting it and Need to order goes up straight away.
        </li>
        <li>
          Why it matters: suppliers sometimes ship only part of a PO and the rest is closed out ("short-closed"). Off-season base
          POs delivered only 57% of what was ordered in 2024–26 (PO 9814: 4,040 ordered, 1,688 received).
        </li>
      </ul>
      <Details summary={`Example (${AS_OF}): GSCUF908-705`}>
        1,140 frames on order, due 7 Feb 2027 — after the 90-day window then in use (to 17 Jan). They no longer count, so
        Need to order showed <b>97</b> instead of 0.
      </Details>
      <H3>Lead times in use</H3>
      <LiveLeadTimes />
      <p>The reasons for these values are in section 8.</p>
      <H3>Safety buffer</H3>
      <LiveSafety />
      <ul>
        <li>
          <b>Size per SKU</b> — bigger when it sells more, and bigger when its past forecasts were further off; longer lead
          times get a larger buffer.
        </li>
        <li>
          <b>95% fill rate</b> means about 95% of demand ships from stock; in a busy stretch around 5% of units may wait.
        </li>
        <li>
          The multiplier comes from our own forecast misses, not a textbook formula — ours are mostly small with occasional very
          big orders, and the textbook value would have covered only ~86% of periods.
        </li>
        <li>
          The stricter setting (never run out in 95% of periods) roughly doubles the stock held.
        </li>
      </ul>
    </>
  );
}

function LeadTimes() {
  return (
    <>
      <H2 id="leadtimes">8. Lead times: what the data shows</H2>
      <p>
        In October 2026 we measured how long POs actually took, from NetSuite PO date to receipt, for every frame and fabric
        PO since 2023 (receipts tie out to NetSuite exactly). The settings in section 7 come from this.
      </p>

      <Callout>
        <b>How to read the tables:</b> "44 / 59 / 71 days" means half of the units arrived within 44 days of ordering, three in
        four within 59, and nine in ten within 71. The last column is the share that took longer than the lead time.
      </Callout>
      <H3>Frames</H3>
      <Table
        head={["PO type", "Typical arrival (half / 3 in 4 / 9 in 10 of units)", "Over 90 days"]}
        rows={[
          [<span><b>In-season re-orders</b> (placed Mar–Jul), 2024–26</span>, "44 / 59 / 71 days", "9%"],
          [<span><b>Off-season orders</b> (placed Sep–Feb)</span>, "137 / 148 / 165 days", "86%"],
          [<span><b>August pre-season programme</b></span>, "Released Oct → Jun in 40+ containers (median 113–155 days)", "—"],
        ]}
      />
      <ul>
        <li>
          <b>In season, frames come fast only because Smart Fit already has them.</b> Re-orders ship a median 13 days after the
          PO, from stock built for the season; sea plus port to receipt is a steady ~30 days. That's why in-season is{" "}
          <b>90 days</b> — it covers 91% of units, and the slack is the only cushion for a late container.
        </li>
        <li>
          <b>A frame that needs new production takes ~4½–6 months door to door.</b> That's why off season is <b>150 days</b>{" "}
          — and why a SKU outside the vendor's season programme can behave like off season even in summer (use a per-SKU
          override).
        </li>
        <li>
          <b>2023 shows the risk:</b> with less vendor stock, in-season orders took a median 87 days and 47% took over 90.
        </li>
        <li>
          <b>The August PO is a delivery schedule, not a re-order.</b> It's ~85% of Mar–Aug frame units. Need to order times
          each SKU's share by its expected date (section 7).
        </li>
        <li>
          <b>NetSuite's expected dates aren't reliable for frames.</b> On re-orders they're placeholders 4–5 months out
          (goods arrive ~3 months earlier); on the August programme only 16% of units land within two weeks of them. Synkio's
          ETA + ~9 days is accurate once a container ships.
        </li>
      </ul>

      <H3>Solstice (Olefin) and Pacifica Marine covers</H3>
      <Table
        head={["PO type", "Typical arrival (half / 3 in 4 / 9 in 10 of units)", "Over 90 days"]}
        rows={[
          [<span><b>Off-season orders</b> (placed Sep–Feb)</span>, "139 / 152 / 164 days — same as frames", "87%"],
          [<span><b>In-season re-orders</b> (placed Mar–Jul), 2024–26</span>, "80 / 121 / 121 days", "41%"],
          [<span><b>August pre-season programme</b></span>, "Released autumn → spring, like frames", "—"],
        ]}
      />
      <ul>
        <li>
          These covers are bought finished, almost all from Smart Fit, and ride the same containers as frames (193 of 204
          containers that brought covers also brought frames). We never sew them or order their fabric.
        </li>
        <li>
          <b>Off season they behave exactly like frames: 150 days.</b>
        </li>
        <li>
          <b>In season they're slower than frames: 120 days.</b> Smart Fit ships frames from stock it holds for the season, but
          makes covers to order — in 2024 covers shipped a median 41 days after the PO, against 9–18 days for frames.
        </li>
        <li>
          <b>C118 covers (11 ft) are always last</b> in every programme, so they have a 150-day override all year.
        </li>
        <li>
          Covers coded <Code>FD</Code> (e.g. <Code>C908-FD10</Code>) are Olefin designs and are treated the same way.
        </li>
        <li>
          Sunbrella covers, sewn in-house in days, keep their short lead times and no buffer.
        </li>
      </ul>
      <H3>Bases</H3>
      <Table
        head={["PO type", "Typical arrival (half / 3 in 4 / 9 in 10 of units)", "Over 90 days"]}
        rows={[
          [<span><b>In-season re-orders</b> (placed Mar–Jul), 2024–26</span>, "50 / 74 / 94 days", "14%"],
          [<span><b>Off-season orders</b> (placed Sep–Feb)</span>, "123 / 139 / 176 days", "85%"],
          [<span><b>August pre-season programme</b></span>, "Released through spring (median 114–191 days)", "—"],
        ]}
      />
      <ul>
        <li>
          All bases come from Smart Fit, mostly on the same containers as frames (228 of 255). Nothing is made in the US,
          whatever NetSuite's "country of manufacture" field says.
        </li>
        <li>
          <b>In season: 90 days</b>, like frames (2025–26 shipped from stock; 2024 was made to order and slower).{" "}
          <b>Off season: 150 days.</b>
        </li>
        <li>
          <b>Watch for short deliveries:</b> off-season base POs delivered only 57% of what was ordered (e.g. PO 9814: 4,040
          ordered, 1,688 received). Treat an open base PO with some caution.
        </li>
        <li>
          Trim (binding, fringe, tassels) and protective covers used to be counted as bases. They're now their own categories,
          so base forecasts, pace and buffers reflect real bases only.
        </li>
      </ul>
      <H3>Fabric</H3>
      <Table
        head={["PO type", "Typical arrival (half / 3 in 4 / 9 in 10 of yards)", "Over 14 days"]}
        rows={[
          [<span><b>In-season re-orders</b> (under 5,000 yd, Mar–Aug), 2024–26</span>, "9 / 12 / 15 days", "10%"],
          [<span><b>Off-season re-orders</b> (Sep–Feb)</span>, "10 / 14 / 20 days", "25%"],
          [<span><b>Glen Raven bulk POs</b> (5,000 yd+, ~3 a year)</span>, "45 / 60 / 78 days", "91%"],
        ]}
      />
      <ul>
        <li>
          <b>In season: 14 days</b> (97% of yards arrive within 21). <b>Off season: 21 days.</b>
        </li>
        <li>
          Vendors are close: Trivantage (cut lengths) ~4–5 days, Glen Raven (rolls) ~10–13 days.
        </li>
        <li>
          <b>Bulk POs are stocking orders, not re-orders</b> — fabrics only ever bought that way (5405, 5407) have a 60-day
          override. <b>7244</b> (Trivantage) has been slow repeatedly and has a 30-day override.
        </li>
        <li>
          <b>The real cause of fabric stockouts was ordering late, not slow suppliers.</b> 67% of in-season re-orders were
          placed after the fabric had already run out; a typical stockout lasted about a week (order ~4 days after hitting zero,
          arrival ~4 days later). That's why fabric now carries a safety buffer — so Need to order fires <i>before</i> the
          shelf is empty.
        </li>
      </ul>
      <Callout>
        <b>Re-check yearly.</b> These findings rest on a few dozen POs from two frame vendors and two fabric vendors. If a
        vendor's behaviour changes — e.g. Smart Fit stops holding season stock — in-season frame lead times jump toward the
        off-season figure.
      </Callout>
    </>
  );
}

function Stockouts() {
  return (
    <>
      <H2 id="stockouts">9. Stockouts and lost sales</H2>
      <p>History shows what shipped, not what customers wanted. Two tools fill the gap:</p>
      <ul>
        <li>
          <b>Stockout calendar</b> — each day a SKU couldn't be supplied, from NetSuite's <b>Available to Sell</b> (on the shelf
          plus what can still be sewn or assembled from parts in stock). Zero = genuinely unavailable.
        </li>
        <li>
          <b>Cancelled orders</b> — sales-order units cancelled or closed without shipping, broken down through the parts list.
          Intercompany orders left out. In 2025: frames lost 7,840 units against 81,595 shipped (10%); fabric 29,641 yards (15%).
        </li>
      </ul>
      <p>
        Both are columns in Yearly Demand → One part. Cancelled orders feed Fabric's forecast; adjusting history for stockout days
        was tested and didn't help accuracy.
      </p>
    </>
  );
}

function Routine() {
  return (
    <>
      <H2 id="routine">10. A simple routine</H2>
      <ul>
        <li>
          <b>Daily</b> — open <To to="/">Home</To>, then <To to="/order">To order</To> (buying) or{" "}
          <To to="/arriving">Arriving</To> (receiving). Raise POs for <b>Order now</b>, ask suppliers to bring <b>Ship sooner</b>{" "}
          deliveries forward, and chase <b>late</b> and <b>stalled</b> POs. If the "Updated" time at the top looks old, check{" "}
          <To to="/overview">Data status</To> — today's numbers may be stale.
        </li>
        <li>
          <b>Weekly</b> — <To to="/trends">Trends</To> for what customers are moving toward; the tie-out badge on Yearly
          Demand should be green.
        </li>
        <li>
          <b>Monthly</b> — <To to="/accuracy">Forecast accuracy</To>: is the current window on track?
        </li>
        <li>
          <b>When a number looks wrong</b> — click the row for its panel (Forecast, To order), click a total (Yearly
          Demand), or open One part and a month to see the invoices.
        </li>
        <li>
          <b>Ops habit</b> — set real expected dates on POs in NetSuite, and close a PO line as soon as you know the rest isn't
          coming. Arrival timing and stalled flags are only as good as those dates until a container ships.
        </li>
      </ul>
    </>
  );
}

function AI() {
  return (
    <>
      <H2 id="ai">11. AI assistants and the API</H2>
      <p>
        Claude (or another AI assistant) can read the app for you — "what needs attention today?" without opening every
        screen. It sees the same numbers as the website, and only what <b>your</b> account can see. Read only.
      </p>
      <H3>Connect Claude (once)</H3>
      <ol>
        <li>
          In Claude: <b>Settings → Connectors → Add custom connector</b>.
        </li>
        <li>
          URL: <Code>https://forecast-api.marchproducts.com/mcp</Code>
        </li>
        <li>
          Click <b>Allow</b>, then sign in with your March Microsoft account.
        </li>
      </ol>
      <Table
        head={["Tool", "Answers"]}
        rows={[
          [<b>What to order</b>, "Need to order plus order / expedite? tags, with what arrives within and after the lead time"],
          [<b>Forecast</b>, "90-day forecast and supply for a category or SKUs"],
          [<b>Trends</b>, "What customers are choosing — by finish, series, size, cover line or colour — against the same weeks last year (numbers only)"],
          [<b>Stock and recent use</b>, "Stock by warehouse, on order, last 30 days' use and days of stock at that pace (March Products or Gold Crest); no advice"],
          [<b>SKU detail</b>, "Forecast, stock, monthly usage, stockouts, cancelled orders, confidence"],
          [<b>Forecast accuracy</b>, "Is this window on track; track record; model scores"],
          [<b>Data freshness</b>, "When each feed refreshed; last night's NetSuite tie-out"],
          [<b>Planning settings</b>, "Lead times and buffer targets"],
          [<b>Stalled purchase orders</b>, "Stock on order more than 30 days late and not shipped — what to chase"],
          [<b>SKU transactions</b>, "The invoices behind a number (live from NetSuite)"],
        ]}
      />
      <Callout>
        <b>Good questions:</b> "What needs ordering or expediting today?" · "Is anything newly urgent?" · "How are bases pacing?" ·
        "Why is ALTO908-002's forecast what it is?" · "Is the data fresh?"
      </Callout>
      <H3>API keys</H3>
      <p>
        A script or system that can't sign in uses an <b>API key</b>, created by an admin on Admin → Agent access (shown once;
        read only or admin; revoke any time). Send it as <Code>Authorization: Bearer mia_…</Code> to{" "}
        <Code>https://forecast-api.marchproducts.com/api/…</Code> or the <Code>/mcp</Code> address. Treat it like a password.
        Admin → Agent access also lists sign-ins and every assistant tool call, kept 90 days.
      </p>
    </>
  );
}

function Glossary() {
  const rows: Array<[string, string]> = [
    ["SKU", "An item code, e.g. ALTO908-002"],
    ["PO", "Purchase order"],
    ["Lead time", "Days from ordering to usable stock — set per category, off and in season (section 8)"],
    ["In season", "March–August (set on Admin → Lead times)"],
    ["Window", "The 90-day period a forecast covers"],
    ["Units used", "Every unit that left the building, alone or inside an umbrella"],
    ["Sold on its own", "Sold by itself, not inside an umbrella — all NetSuite's sales reports show"],
    ["BOM / parts list", "An umbrella's bill of materials"],
    ["Cover pattern", "A Sunbrella cover size + fabric (e.g. C908-5401) adding together its standard versions"],
    ["Trim", "Binding, fringe and tassels for covers, bought in yards (codes like BDNATURAL, FT-FRN11-SA04)"],
    ["Protective cover", "A storage cover for a closed umbrella (PC301–PC315, UC100–UC102)"],
    ["Solstice / Pacifica Marine", "Cover lines bought finished from overseas; NetSuite calls the fabrics Olefin and Pacifica (codes like C908-F04, C908-FD10, C908-SA04)"],
    ["Order now", "Runs out before a new order could arrive: raise a PO (To order, Forecast)"],
    ["Ship sooner", "On order, but it lands after the shelf runs empty: ask the supplier to bring it forward"],
    ["Late PO / Late delivery", "A delivery past its expected date and not received (includes stalled POs)"],
    ["Top-up", "Below the safety buffer only; can wait"],
    ["Share / share moved", "Trends: a value's part of its category's units, and how many points that changed from the same weeks last year"],
    ["QOH / Current stock", "Quantity on hand"],
    ["Burn", "Units used over a period (the part panel: last 30 days)"],
    ["ETA", "Estimated arrival of a container (Synkio); receipt is typically ~9 days later"],
    ["Stalled PO", "Stock on order more than 30 days past its expected date and not shipped — flagged, still counted"],
    ["Short-closed", "A PO line closed before everything arrived; the rest was cancelled"],
    ["Pre-season programme", "The large August frame PO, delivered in releases through June"],
    ["Bulk PO (fabric)", "A Glen Raven order of 5,000+ yards; a stocking run, not a re-order"],
    ["Top-down split", "Forecast the category, then share it out by recent share"],
    ["Pace", "Last 28 days of actual use ÷ what the forecast expected"],
    ["Safety buffer", "Extra stock to cover a busier-than-forecast stretch"],
    ["Fill rate", "Share of demand shipped from stock rather than waiting"],
    ["Weighted error", "Total units missed ÷ total units used; lower is better"],
    ["Backtest", "Re-running a model on past dates using only the data known then"],
    ["Available to Sell", "NetSuite field: on shelf + what can be built from parts in stock"],
    ["Fiscal year", "July–June, named for the year it ends (FY2026 = Jul 2025 – Jun 2026)"],
    ["MCP / connector", "How an AI assistant connects to the app"],
    ["API key", "A password-like key that lets a script read the app"],
  ];
  return (
    <>
      <H2 id="glossary">12. Glossary</H2>
      <Table head={["Term", "Meaning"]} rows={rows.map(([t, m]) => [<b>{t}</b>, m])} />
    </>
  );
}

function Faq() {
  return (
    <>
      <H2 id="faq">13. Common questions</H2>
      <Details summary="Why is the app's number so much higher than NetSuite's sales reports?">
        NetSuite's sales reports count a part only when it's sold by itself. The app also counts parts that went out inside
        umbrellas — most of them. Click any total in Yearly Demand → Every part for the split.
      </Details>
      <Details summary="Forecast use is red, but Need to order is 0. Why?">
        The red part of the bar is the next 90 days' use that the stock on the shelf doesn't cover. Need to order looks one lead
        time ahead and counts deliveries that land in time, so a red bar with Need to order 0 means orders already on the way
        cover it. Check the On order dates and Runs out.
      </Details>
      <Details summary="Why are some parts missing from To order?">
        Operations asked for them to be left off: they aren't monitored or bought for stock (the list is on Admin → Not
        stocked). Each page says how many are left
        out and has a <b>Show them</b> link. The Forecast page still lists them, and AI agents' buying list leaves them out
        the same way.
      </Details>
      <Details summary="Lots is on order, so why does Need to order say to buy more?">
        Some of what's on order lands after the lead time — typically later releases of the August pre-season PO. Click the part to see
        what arrives when. Often the better move is asking the supplier to bring a release forward.
      </Details>
      <Details summary="Need to order jumped in September (or dropped in March). Why?">
        Lead times change with the season — off season (Sep–Feb) frames, bases and imported covers take about 150 days, and
        fabric 21; in season (Mar–Aug) frames and bases 90, imported covers 120 and fabric 14. A longer lead time looks further
        ahead and needs a bigger buffer. Section 7 shows the current values.
      </Details>
      <Details summary="Why does Need to order say 0 when I know we'll need more for summer?">
        It looks only as far ahead as the lead time. We order on demand, so a summer shortage is flagged when it comes within
        the lead time — exactly when the order needs to go in. The pre-season buy itself is planned outside the app.
      </Details>
      <Details summary="A base forecast changed overnight with no sales. Why?">
        Bases follow category pace: if bases as a whole ran above or below forecast over the last 28 days, every base forecast
        moves a little. Hover the 90-day forecast in Forecast → More detail for the multiplier.
      </Details>
      <Details summary={'Why does a PO say "No ETA", or the date look wrong?'}>
        "No ETA" = no expected date was set on the PO in NetSuite. Expected dates are often placeholders until a container
        ships; then Synkio's ETA takes over. Real dates on POs make the timing flags reliable.
      </Details>
      <Details summary="Why don't Gold Crest numbers appear in the forecast?">
        Gold Crest LLC is a separate company; its stock and sales are kept apart. It isn't shown in the app for now.
      </Details>
      <Details summary="I can't sign in, or the page won't load.">
        Use your March Microsoft account. Guest accounts can't sign in. If it persists, tell an admin.
      </Details>
      <Details summary={'What does "PO stalled" mean?'}>
        Stock on order whose expected date passed more than 30 days ago, with nothing on a container. It still counts as
        supply, so if it isn't really coming, Need to order is too low. Chase the supplier, then fix the date or close the line
        in NetSuite.
      </Details>
      <Details summary={'What is a "short-closed" PO?'}>
        A PO line closed before everything arrived — the rest was cancelled. Until someone closes it, the app counts the
        missing stock as coming; the "PO stalled" badge is the early warning.
      </Details>
      <Details summary="Can the AI assistant change anything?">No. Its tools only read.</Details>
    </>
  );
}

function Limits() {
  return (
    <>
      <H2 id="limits">14. Known limits</H2>
      <ul>
        <li>
          <b>PO timing.</b> Until a container ships, arrival comes from the PO's expected date, and NetSuite keeps one date per
          SKU on a PO — not one per release. Many dates are placeholders.
        </li>
        <li>
          <b>Stalled stock still counts.</b> Stock flagged "PO stalled" is counted as supply until someone fixes the date or
          closes the line, so if it isn't coming, Need to order is too low. (Flag-only while we see how many flags are real.)
        </li>
        <li>
          <b>Trim and protective covers</b> became separate categories in October 2026, so their forecast track record and
          buffer calibration are short.
        </li>
        <li>
          <b>The buffer covers forecast error, not late suppliers.</b> The slack in the in-season frame lead time is the only
          cushion for a late container.
        </li>
        <li>
          <b>Pre-season buying isn't in the app.</b> The forecast looks 90 days ahead; the season's big orders still come from
          the planning workbook.
        </li>
        <li>
          <b>Sunbrella covers carry no buffer</b> and sewing capacity isn't modelled.
        </li>
        <li>
          <b>Gold Crest</b> isn't shown for now, while the March Products pages are finished.
        </li>
        <li>
          <b>History starts July 2023</b>, so backtests cover about five independent quarters — enough to choose between models,
          not to fine-tune them.
        </li>
      </ul>
    </>
  );
}
